import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { QueryKey } from '@/Config/api/queryKey';
import { ActionsApi } from '@/Domain/api/Action/ApiAction/Actions.api';
import { ActionDto, HttpActionDto } from '@/Domain/api/Action/dto/Action.dto';
import { ErrorResponse } from '@/Domain/api/App/dto/Error.dto';
import { getValidationCheckMessage } from '@/Domain/helper/actionValidation.helper';

export type UpdateActionForm = Partial<{
  name: string;
  description: string;
  icon: string;
  icon_color: string;
  type: string;
  is_active: boolean;
}>;

export const useUpdateActionDetails = () => {
  const { t } = useTranslation('actions_page');
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationKey: [QueryKey.actions.basic.patch],
    mutationFn: ({
      actionId,
      data,
    }: {
      actionId: number;
      showToast: boolean;
      data: UpdateActionForm;
    }) => ActionsApi.update(actionId, data),
    onSuccess: (updatedAction, { actionId, showToast }) => {
      if (showToast && updatedAction.is_active) {
        toast.success(t('tooltip.action_enabled'));
      }
      if (showToast && !updatedAction.is_active) {
        toast.info(t('tooltip.action_disabled'));
      }
      void queryClient.invalidateQueries({ queryKey: ['subscription'] });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.actions.basic.health] });
      void queryClient.setQueryData(
        [QueryKey.actions.httpRequest.get, actionId],
        (prev: HttpActionDto) => ({ ...prev, ...updatedAction }),
      );
      void queryClient.setQueryData<ActionDto[]>([QueryKey.actions.basic.list], (old) => {
        if (!old) return [updatedAction];
        return old.map((item) => (item.id === updatedAction.id ? updatedAction : item));
      });
    },
  });

  const handleUpdateDetails = (actionId: number, data: UpdateActionForm, showToast = false) =>
    mutation.mutateAsync({ data, showToast, actionId });

  // Callers catch locally rather than sharing an onError, because two of them also branch
  // on the rejection. validation_checks is preferred over the server message because the
  // latter is untranslated, and on the activation path it reads "please make inactive".
  const updateActionErrorMessage = (error: unknown) => {
    const firstError = (error as AxiosError<ErrorResponse>)?.response?.data?.errors?.root?.[0];
    const validationChecks = firstError?.options?.validation_checks;
    return (
      (validationChecks && getValidationCheckMessage(validationChecks, t)) ||
      firstError?.message ||
      t('toast.update_failed')
    );
  };

  return { handleUpdateDetails, updateActionErrorMessage, isActionPending: mutation.isPending };
};
